import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';

interface CategoryEntry { label: string; active: boolean; files: string[]; }
interface Manifest { [category: string]: CategoryEntry; }

interface Props {
  activeCategory: string | null;
  onCategoryChange: (cat: string | null) => void;
}

const ImageGallery: React.FC<Props> = ({ activeCategory, onCategoryChange }) => {
  const [manifest, setManifest] = useState<Manifest>({});
  const [lightbox, setLightbox] = useState<number | null>(null);
  const savedScroll = React.useRef(0);
  const navigate = useNavigate();
  const location = useLocation();
  const [fromProducts, setFromProducts] = useState((location.state as any)?.fromProducts === true);

  useEffect(() => {
    fetch('/images/gallery/manifest.json')
      .then(r => r.json())
      .then((data: Manifest) => setManifest(data));
  }, []);

  const categories = Object.keys(manifest)
    .filter(k => manifest[k].active && manifest[k].files.length > 0)
    .sort((a, b) => manifest[a].label.localeCompare(manifest[b].label));

  const activeFiles = activeCategory ? manifest[activeCategory]?.files ?? [] : [];
  const activeLabel = activeCategory ? manifest[activeCategory]?.label ?? activeCategory : '';

  const openCategory = (cat: string) => {
    savedScroll.current = window.scrollY;
    onCategoryChange(cat);
    setFromProducts(false);
    window.scrollTo({ top: 0, behavior: 'instant' as ScrollBehavior });
  };

  const backToCategories = () => {
    onCategoryChange(null);
    setLightbox(null);
    setTimeout(() => window.scrollTo({ top: savedScroll.current, behavior: 'smooth' }), 50);
  };

  const closeLightbox = useCallback(() => setLightbox(null), []);
  const prev = useCallback(() => setLightbox(i => i !== null ? (i - 1 + activeFiles.length) % activeFiles.length : null), [activeFiles.length]);
  const next = useCallback(() => setLightbox(i => i !== null ? (i + 1) % activeFiles.length : null), [activeFiles.length]);

  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') { lightbox !== null ? closeLightbox() : onCategoryChange(null); }
      if (e.key === 'ArrowLeft') prev();
      if (e.key === 'ArrowRight') next();
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [closeLightbox, prev, next, lightbox]);

  if (!categories.length) return <p style={{ textAlign: 'center', color: 'var(--color-text-muted)', padding: '3rem' }}>Loading gallery…</p>;

  if (!activeCategory) {
    return (
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(240px, 260px))', gap: '16px', justifyContent: 'center' }}>
        {categories.map(cat => {
          const { label, files } = manifest[cat];
          return (
            <div
              key={cat}
              role="button"
              tabIndex={0}
              onClick={() => openCategory(cat)}
              onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); openCategory(cat); } }}
              aria-label={`View ${label} gallery, ${files.length} photos`}
              className="gallery-thumb"
              style={{ borderRadius: '10px', overflow: 'hidden', cursor: 'pointer', position: 'relative', background: 'var(--color-text)', aspectRatio: '4/3' }}
            >
              <picture>
                <source srcSet={`/images/gallery/${cat}/${files[0].replace(/\.(jpg|jpeg|png)$/i, '.avif')}`} type="image/avif" />
                <img
                  src={`/images/gallery/${cat}/${files[0]}`}
                  alt={`${label} installation example`}
                  loading="lazy"
                  style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block', opacity: 0.75, transition: 'transform 0.3s, opacity 0.3s' }}
                />
              </picture>
              <div aria-hidden="true" style={{ position: 'absolute', inset: 0, display: 'flex', flexDirection: 'column', justifyContent: 'flex-end', padding: '1rem', background: 'linear-gradient(transparent 40%, rgba(0,0,0,0.7))' }}>
                <div style={{ color: 'white', fontWeight: 700, fontSize: '1rem' }}>{label}</div>
                <div style={{ color: 'rgba(255,255,255,0.7)', fontSize: '0.8rem' }}>{files.length} photos</div>
              </div>
            </div>
          );
        })}
      </div>
    );
  }

  return (
    <div>
      <div className="gallery-back-buttons" style={{ position: 'fixed', top: '136px', left: '50%', transform: 'translateX(-50%)', zIndex: 99, display: 'flex', gap: '0.75rem' }}>
        <button
          onClick={backToCategories}
          style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', background: 'white', border: '1px solid var(--color-border)', borderRadius: '50px', color: 'var(--color-primary)', fontWeight: 600, fontSize: '0.95rem', cursor: 'pointer', padding: '0.5rem 1.1rem', boxShadow: '0 2px 8px rgba(0,0,0,0.08)', whiteSpace: 'nowrap' }}
        >
          Back to categories
        </button>
        {fromProducts && (
          <button
            onClick={() => { navigate('/products'); window.scrollTo({ top: 0, behavior: 'instant' as ScrollBehavior }); }}
            style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', background: 'white', border: '1px solid var(--color-border)', borderRadius: '50px', color: 'var(--color-primary)', fontWeight: 600, fontSize: '0.95rem', cursor: 'pointer', padding: '0.5rem 1.1rem', boxShadow: '0 2px 8px rgba(0,0,0,0.08)', whiteSpace: 'nowrap' }}
          >
            Back to products
          </button>
        )}
      </div>
      <h3 style={{ color: 'var(--color-text)', marginBottom: '1.5rem' }}>{activeLabel} <span style={{ color: 'var(--color-text-muted)', fontWeight: 400, fontSize: '0.9rem' }}>({activeFiles.length} photos)</span></h3>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))', gap: '10px' }}>
        {activeFiles.map((file, i) => (
          <div
            key={file}
            role="button"
            tabIndex={0}
            onClick={() => setLightbox(i)}
            onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setLightbox(i); } }}
            aria-label={`Open ${activeLabel} photo ${i + 1} of ${activeFiles.length}`}
            className="gallery-thumb"
            style={{ borderRadius: '8px', overflow: 'hidden', cursor: 'zoom-in', position: 'relative', aspectRatio: '4/3', background: 'var(--color-border)' }}
          >
            <picture>
              <source srcSet={`/images/gallery/${activeCategory}/${file.replace(/\.(jpg|jpeg|png)$/i, '.avif')}`} type="image/avif" />
              <img
                src={`/images/gallery/${activeCategory}/${file}`}
                alt={`${activeLabel} installation ${i + 1} of ${activeFiles.length}`}
                style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block', transition: 'transform 0.3s' }}
              />
            </picture>
          </div>
        ))}
      </div>

      {lightbox !== null && (
        <div
          role="dialog"
          aria-modal="true"
          aria-label={`${activeLabel} lightbox, photo ${lightbox + 1} of ${activeFiles.length}`}
          onClick={closeLightbox}
          style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.93)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center' }}
        >
          <picture>
            <source srcSet={`/images/gallery/${activeCategory}/${activeFiles[lightbox].replace(/\.(jpg|jpeg|png)$/i, '.avif')}`} type="image/avif" />
            <img
              src={`/images/gallery/${activeCategory}/${activeFiles[lightbox]}`}
              alt={`${activeLabel} ${lightbox + 1} of ${activeFiles.length}`}
              onClick={e => e.stopPropagation()}
              style={{ maxHeight: '88vh', maxWidth: '92vw', borderRadius: '8px', objectFit: 'contain', boxShadow: '0 25px 60px rgba(0,0,0,0.6)' }}
            />
          </picture>
          <button aria-label="Previous image" onClick={e => { e.stopPropagation(); prev(); }} style={{ ...navBtn, left: 0 }}>&#8249;</button>
          <button aria-label="Next image" onClick={e => { e.stopPropagation(); next(); }} style={{ ...navBtn, right: 0 }}>&#8250;</button>
          <button aria-label="Close lightbox" onClick={closeLightbox} style={{ position: 'absolute', top: '1rem', right: '1rem', background: 'rgba(0,0,0,0.5)', border: 'none', color: 'white', fontSize: '1.5rem', cursor: 'pointer', borderRadius: '50%', width: '2.2rem', height: '2.2rem', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>✕</button>
          <div aria-hidden="true" style={{ position: 'absolute', bottom: '1.25rem', color: 'rgba(255,255,255,0.65)', fontSize: '0.85rem' }}>
            {activeLabel} · {lightbox + 1} / {activeFiles.length}
          </div>
        </div>
      )}
    </div>
  );
};

const navBtn: React.CSSProperties = {
  position: 'absolute', top: '50%', transform: 'translateY(-50%)',
  background: 'rgba(0,0,0,0.4)', border: 'none', color: 'white',
  fontSize: '2.5rem', cursor: 'pointer', padding: '0.5rem 0.9rem',
  lineHeight: 1, userSelect: 'none', borderRadius: '6px', zIndex: 1,
};

export default ImageGallery;
